// Interactive world map: pin selection, the place card (a bottom sheet on
// phones, a side panel on larger screens), prev/next/random, search, "show my
// area", saving a place to the reading list and deep links (/#place-<slug>).
import { $, $$, readJSON, t, toast } from "./dom.js";
import { readingList } from "./reading-list.js";
import { swapIn } from "../motion.js";

export function initMap({ signal } = {}) {
  const places = readJSON(".js-places-data");
  const card = $(".js-place-card");
  if (!places || !places.length || !card) return null;

  const pins = $$(".pin");
  const search = $(".js-map-search");
  const results = $(".js-map-results");
  let active = -1;

  const hideSearch = () => {
    results.hidden = true;
    results.innerHTML = "";
  };

  const refreshSave = () => {
    if (active < 0) return;
    const on = readingList.hasPlace(places[active].id);
    const button = $(".js-pc-save", card);
    button.classList.toggle("is-saved", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
    $(".js-pc-save-label", card).textContent = on ? t("saved") : t("save");
  };

  const clearActive = () => {
    active = -1;
    pins.forEach((p) => p.classList.remove("is-active", "is-dim"));
  };

  const closeCard = () => {
    card.classList.remove("is-open");
    clearActive();
  };

  const openPlace = (i) => {
    active = i;
    const p = places[i];
    $(".js-pc-region", card).textContent = p.region;
    $(".js-pc-title", card).textContent = p.name;
    $(".js-pc-country", card).textContent = p.country;
    $(".js-pc-teaser", card).textContent = p.teaser;
    $(".js-pc-gtk", card).textContent = p.gtk;
    $(".js-pc-media", card).innerHTML = p.media;
    const read = $(".js-pc-read", card);
    read.hidden = !p.story;
    read.href = p.story || "#";
    card.classList.add("is-open");
    pins.forEach((pin) => {
      const index = Number(pin.dataset.index);
      pin.classList.toggle("is-active", index === i);
      pin.classList.toggle("is-dim", index !== i);
    });
    refreshSave();
    hideSearch();
    swapIn($$(".place-card__body > *", card), { y: 12, stagger: 0.045, duration: 0.4 });
  };

  const scrollToMap = () => {
    const map = $("#map");
    if (map) map.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Pins
  pins.forEach((pin) => {
    const open = () => openPlace(Number(pin.dataset.index));
    pin.addEventListener("click", open);
    pin.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    });
  });

  // Card controls
  $(".js-pc-close", card).addEventListener("click", closeCard);
  $(".js-pc-prev", card).addEventListener("click", () => openPlace((active - 1 + places.length) % places.length));
  $(".js-pc-next", card).addEventListener("click", () => openPlace((active + 1) % places.length));
  $(".js-random-place").addEventListener("click", () => openPlace(Math.floor(Math.random() * places.length)));
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Escape" && card.classList.contains("is-open")) closeCard();
    },
    { signal }
  );

  $(".js-pc-save", card).addEventListener("click", () => {
    if (active < 0) return;
    const added = readingList.togglePlace(places[active]);
    toast(added ? t("savedToast") : t("removedToast"));
    refreshSave();
  });

  // Search
  const escHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  search.addEventListener("input", () => {
    const q = search.value.trim().toLowerCase();
    if (!q) return hideSearch();
    const hits = places.map((p, i) => ({ p, i })).filter(({ p }) => (p.name + " " + p.country + " " + p.region).toLowerCase().includes(q));
    results.hidden = false;
    if (!hits.length) {
      results.innerHTML = '<div class="empty">' + escHtml(t("noPlaces")) + " “" + escHtml(q) + "”.</div>";
      return;
    }
    results.innerHTML = hits.map(({ p, i }) => '<button type="button" data-i="' + i + '"><span>' + escHtml(p.name) + '</span><span class="c">' + escHtml(p.country) + "</span></button>").join("");
    $$("button", results).forEach((b) =>
      b.addEventListener("click", () => {
        openPlace(Number(b.dataset.i));
        search.value = "";
      })
    );
  });
  search.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      const first = $("button", results);
      if (first) first.click();
    }
    if (e.key === "Escape") {
      search.value = "";
      hideSearch();
    }
  });
  document.addEventListener(
    "click",
    (e) => {
      if (!e.target.closest(".map-search")) hideSearch();
    },
    { signal }
  );

  // "Show my area"
  $(".js-locate").addEventListener("click", () => {
    $(".js-you-dot").removeAttribute("hidden");
    toast(t("locate"));
  });

  const byId = (id) => {
    const index = places.findIndex((p) => p.id === id);
    if (index < 0) return;
    scrollToMap();
    openPlace(index);
  };

  // Deep link: /#place-lisbon (from a story's "On the map" button or the reading list)
  const fromHash = () => {
    const m = location.hash.match(/^#place-(.+)$/);
    if (m) setTimeout(() => byId(decodeURIComponent(m[1])), 150);
  };
  fromHash();
  window.addEventListener("hashchange", fromHash, { signal });

  return { byId, refresh: refreshSave };
}
